// XIMA auf Deutsch lernen (2026-10-18): die wichtigsten Anleitungen, übersetzt aus dem Spanischen und Englischen.

export const DE_GUIDES = [
  {
    slug: "de",
    lang: "de",
    nav: "XIMA auf Deutsch",
    title: "XIMA lernen: Regeln, Strategie und Anleitungen auf Deutsch",
    description:
      "Alles, um XIMA zu lernen, das kubanische 3D-Strategiespiel: was es ist, wie man spielt, die offiziellen Regeln, das Brett, die Spielmodi, die Notation und Strategie.",
    h1: "XIMA auf Deutsch",
    intro:
      "XIMA ist ein Strategiebrettspiel, das nach oben gespielt wird: Das Brett ist eine Stufenpyramide mit vier Etagen, und es gewinnt, wer als Erster seinen Spezialblock auf die Spitze bringt, das Feld G7. Diese Anleitungen führen dich von der ersten Partie bis zur fortgeschrittenen Strategie.",
    sections: [
      { h: "Hier anfangen", links: ["de/what-is-xima", "de/how-to-play", "de/rules", "de/history-of-xima"] },
      { h: "Regeln im Detail", links: ["de/board", "de/climbing", "de/capturing", "de/game-modes", "de/notation"] },
      { h: "Besser spielen", links: ["de/strategy", "de/openings", "de/cimax"] },
      {
        h: "Jetzt spielen",
        p: [
          "XIMA kannst du kostenlos auf juegoxima.com spielen, am Computer oder Handy, gegen Menschen, gegen den Bot (vier Schwierigkeitsstufen), in Turnieren oder mit dem täglichen Rätsel. Die Oberfläche gibt es auch auf Deutsch: Stelle sie in den Einstellungen um.",
        ],
      },
    ],
    es: null,
  },
  {
    slug: "de/what-is-xima",
    lang: "de",
    nav: "Was ist XIMA",
    title: "Was ist XIMA? Das Strategiebrettspiel in 3D",
    description:
      "XIMA ist ein kubanisches 3D-Brettspiel: eine Pyramide mit 4 Etagen, 10 Blöcke pro Spieler und ein Ziel – den Spezialblock auf die Spitze bringen. Kostenlos online spielen.",
    h1: "Was ist XIMA?",
    intro:
      "XIMA ist ein Strategiebrettspiel in drei Dimensionen. Das Brett ist eine Stufenpyramide mit vier Etagen, und es gewinnt, wer als Erster seinen Spezialblock auf die Spitze (G7) bringt. Man kann ganz ohne Glück spielen, wie Schach, oder mit Würfeln, wie „Mensch ärgere dich nicht“.",
    sections: [
      {
        h: "Ein Brett, auf dem man nach oben spielt",
        p: [
          "Von oben ist das Brett ein Raster aus 13 × 13 Feldern (169). Von der Seite ist es eine Pyramide mit vier Etagen und einem einzigen Feld ganz oben: der Spitze.",
          "Die Blöcke bewegen sich nicht nur über das Brett, sie steigen auf und ab. Zum Aufsteigen braucht man eine Stufe: einen anderen Block, eigenen oder gegnerischen. Jede Partie ist deshalb ein Wettlauf: Wege nach oben bauen und die Wege des Gegners abschneiden.",
        ],
        figure: "tablero-vista-superior",
      },
      {
        h: "Die Figuren: zehn gleiche Blöcke",
        p: [
          "Jeder Spieler hat 10 Blöcke seiner Farbe: neun normale und einen Spezialblock. Alle ziehen gleich, geradeaus in eine der acht Richtungen. Nur der Spezialblock kann durch Erreichen der Spitze gewinnen.",
          "Zu Beginn stehen alle Blöcke neben dem Brett. In deinem Zug bringst du entweder einen neuen Block ins Spiel oder bewegst einen, der schon auf dem Brett steht.",
        ],
      },
      {
        h: "Beherrschen und Schlagen",
        p: [
          "Beendet ein Block seinen Zug auf einem anderen, beherrscht er ihn: Der untere Block kann nicht ziehen, solange er bedeckt ist. Ist deine Farbe in dieser Säule danach in der Mehrheit, werden die Blöcke in der Minderheit geschlagen und verlassen das Spiel.",
        ],
      },
      {
        h: "Mit Würfeln, ohne Würfel oder beides",
        p: [
          "XIMA hat vier Modi: „Wissenschaft“ (Ciencia) ohne Würfel, „Zufall“ (Azar) mit zwei Würfeln, „Cienzar“, in dem du in jedem Zug wählst, ob du frei ziehst oder würfelst, und „Wissenschaft gegen Zufall“ (Contrazar), in dem eine Seite ohne und die andere mit Würfeln spielt.",
        ],
      },
      {
        h: "Kein Remis",
        p: [
          "XIMA hat immer einen Gewinner. Gerät eine Partie in eine Sackgasse, gibt es kein Remis: Jeder Spieler bekommt drei zusätzliche normale Blöcke, und es geht weiter.",
        ],
      },
    ],
    facts: [
      ["Spieler", "2 bis 4"],
      ["Brett", "Pyramide mit 4 Etagen, 13 × 13 Felder"],
      ["Figuren", "10 Blöcke pro Spieler: 9 normale, 1 Spezialblock"],
      ["Ziel", "Den Spezialblock auf die Spitze (G7) bringen"],
      ["Modi", "Wissenschaft, Zufall, Cienzar, Wissenschaft gegen Zufall"],
      ["Herkunft", "Havanna, Kuba, 2010"],
    ],
    faqs: [
      ["Was ist das Ziel von XIMA?", "Den eigenen Spezialblock mit einem regelgerechten Zug auf die Spitze des Bretts, das Feld G7, zu bringen. Wer das zuerst schafft, gewinnt."],
      ["Wer hat XIMA erfunden?", "Yosdel Vicente Muiño Acevedo und Liz Power Suárez, 2010 in Havanna (Kuba)."],
      ["Kann man XIMA kostenlos spielen?", "Ja, auf juegoxima.com, im Browser oder auf dem Handy."],
    ],
    related: ["de/how-to-play", "de/rules", "de/board", "de/history-of-xima"],
    cta: { label: "So wird gespielt", slug: "de/how-to-play" },
    es: "que-es-xima",
  },
  {
    slug: "de/how-to-play",
    lang: "de",
    nav: "So wird gespielt",
    title: "XIMA spielen: Anleitung für deine erste Partie",
    description:
      "Lerne XIMA in fünf Minuten: Blöcke einsetzen, geradeaus ziehen, mit Hilfe anderer Blöcke Etagen hochsteigen und die Spitze erreichen.",
    h1: "So wird XIMA gespielt",
    intro: "Hier steht nur, was du für die erste Partie brauchst: was du erreichen musst, was du in deinem Zug tun darfst und wie man zur Spitze kommt.",
    sections: [
      { h: "1. Das Ziel", p: ["Es gewinnt, wer als Erster seinen Spezialblock auf die Spitze bringt, das Feld G7 ganz oben auf der Pyramide."] },
      {
        h: "2. Dein Zug: einsetzen oder ziehen",
        p: ["Du beginnst mit allen 10 Blöcken neben dem Brett. In jedem Zug machst du eins von beiden:"],
        list: [
          "Einsetzen: einen Block von außen aufs Brett bringen. Er kommt vom Rand der Pyramide herein, geradeaus oder diagonal, und darf nicht auf einem anderen Block beginnen oder enden.",
          "Ziehen: einen deiner Blöcke auf dem Brett geradeaus bewegen (waagerecht, senkrecht oder diagonal), so viele Felder du willst.",
        ],
        figure: "incorporar-bloque",
      },
      {
        h: "3. Aufsteigen",
        p: [
          "Jede Etage liegt zwei Blockhöhen über der vorherigen, und ein Block steigt pro Schritt höchstens eine Blockhöhe. Für die nächste Etage brauchst du also eine Stufe: einen Block (eigenen oder gegnerischen) direkt an der höheren Etage, über den du gehen kannst.",
          "Beispiel: Du hast Blöcke auf A1 und B2. Der Block auf A1 zieht diagonal, geht über B2 hinweg und landet auf C3, schon auf der zweiten Etage. B2 war die Stufe. Absteigen geht immer.",
        ],
        figure: "subir-con-escalon",
      },
      {
        h: "4. Beherrschen und Schlagen",
        p: [
          "Beendest du deinen Zug auf einem Block, beherrschst du ihn: Er kann nicht ziehen, solange du ihn bedeckst. Hat danach keine Farbe in der Säule mehr Blöcke als deine, werden alle Farben mit weniger Blöcken geschlagen.",
          "Beispiel: Auf einem roten Block steht ein blauer (1 gegen 1: Rot ist beherrscht, nicht geschlagen). Kommt ein zweiter blauer Block von weiter oben dazu, steht es 2 gegen 1 und Rot ist geschlagen.",
        ],
      },
      {
        h: "5. Der Spezialblock",
        p: [
          "Der Spezialblock zieht wie die anderen, hat aber zwei eigene Regeln: Er darf nie auf den gegnerischen Spezialblock steigen oder über ihn hinweggehen, und nur er gewinnt auf der Spitze. Wird er geschlagen, hast du nicht verloren: Du kannst ihn retten, indem du einen normalen Block auf die Spitze bringst.",
        ],
      },
      { h: "6. Deine erste Partie", p: ["Am einfachsten startest du gegen den Bot auf „Leicht“ im Modus „Wissenschaft“ (ohne Würfel). Danach siehst du dir die Wiederholung im Verlauf an."] },
    ],
    faqs: [
      ["Wie weit darf ein Block ziehen?", "Im Modus „Wissenschaft“ so weit du willst in gerader Linie. Im Modus „Zufall“ genau so weit, wie der Würfel zeigt."],
      ["Darf ein Block das Brett verlassen?", "Nein. Ein Block verlässt das Brett nur, wenn er geschlagen wird."],
    ],
    related: ["de/rules", "de/climbing", "de/capturing", "de/game-modes"],
    cta: { label: "Alle Regeln", slug: "de/rules" },
    es: "como-jugar",
  },
  {
    slug: "de/rules",
    lang: "de",
    nav: "Offizielle Regeln",
    title: "XIMA-Regeln: die 16 offiziellen Regeln erklärt",
    description:
      "Die offiziellen Regeln von XIMA mit Beispielen: Züge, Einsetzen, gerade Züge, Aufsteigen, Beherrschen, Schlagen, Retten des Spezialblocks und Gewinnen.",
    h1: "XIMA-Regeln",
    intro: "XIMA hat 16 offizielle Regeln. Hier sind sie alle.",
    sections: [
      { h: "Das Ziel", p: ["Den Spezialblock mit einem regelgerechten Zug auf die Spitze (G7) bringen. Wer es zuerst schafft, gewinnt."] },
      {
        h: "Die offiziellen Regeln",
        list: [
          "Zu Beginn hat jeder Spieler seine 10 Blöcke (9 normale, 1 Spezialblock) neben dem Brett.",
          "Jeder Spieler zieht einmal pro Zug und darf über jede der vier Seiten der Pyramide einsetzen oder aufsteigen.",
          "In seinem Zug setzt der Spieler entweder einen neuen Block ein oder bewegt einen Block auf dem Brett.",
          "Blöcke ziehen geradeaus in jede Richtung (waagerecht, senkrecht oder diagonal), beliebig viele Schritte oder in den Zufallsmodi so viele, wie die Würfel zeigen.",
          "Beim Einsetzen müssen der erste und der letzte Schritt auf dem Brett sein: Der Block darf nicht auf einem anderen Block beginnen oder enden.",
          "Ist ein Block auf dem Brett, darf er es nicht mehr verlassen.",
          "Pro Schritt darf ein Block höchstens auf seine eigene Höhe steigen und auf jede tiefere Stelle absteigen.",
          "Ein Block, der seinen Zug auf anderen beendet, beherrscht sie: Die unteren können nicht ziehen. Außerdem entfernt er die Blöcke anderer Farben, die in dieser Säule in der Minderheit sind.",
          "Ein Spezialblock darf nicht auf einen anderen Spezialblock steigen oder über ihn hinweggehen.",
          "Wird der Spezialblock eines Spielers geschlagen, darf er einen normalen Block auf die Spitze bringen, um ihn zu retten, und den Spezialblock später jederzeit wieder einsetzen.",
          "Ein normaler Block darf seinen Zug nicht auf der Spitze beenden, außer um den Spezialblock zu retten, darf aber über das Feld G7 hinweggehen.",
          "Wer in seinem Zug keine Blöcke auf oder neben dem Brett hat, verliert (keine Mittel).",
          "Wer in seinem Zug keine Blöcke zum Einsetzen mehr hat und dessen Blöcke auf dem Brett alle beherrscht sind, gewinnt („ahogado“).",
          "Dieselbe Stellung oder Zugfolge darf nicht ein drittes Mal hintereinander wiederholt werden.",
          "Jede Remis- oder Blockadesituation wird durch zusätzliches Material gelöst, nie durch Remis.",
          "Zusätzliches Material bedeutet drei zusätzliche normale Blöcke für jeden Spieler.",
        ],
      },
      {
        h: "Die Höhenregel",
        p: [
          "Das ist der Schlüssel des ganzen Spiels. Jede Etage liegt zwei Blockhöhen über der vorherigen, und ein Block steigt pro Schritt höchstens eine Höhe. Deshalb kommt man nicht direkt von einer Etage zur nächsten: Man muss über einen anderen Block als Stufe gehen.",
        ],
        figure: "tablero-vista-lateral",
      },
      {
        h: "Schlagen",
        p: [
          "Zähle die Blöcke jeder Farbe in der Säule, den gerade angekommenen eingeschlossen. Hat keine Farbe mehr als deine, werden alle Farben mit weniger Blöcken geschlagen. Eins gegen eins ist nur Beherrschen, zwei gegen eins ist Schlagen.",
        ],
        figure: "dominacion-captura",
      },
    ],
    faqs: [
      ["Kann ein normaler Block gewinnen?", "Nein. Nur der Spezialblock gewinnt auf der Spitze."],
      ["Kann eine Partie XIMA unentschieden enden?", "Nein. Gerät die Partie in eine Sackgasse, bekommt jeder Spieler drei zusätzliche Blöcke."],
    ],
    related: ["de/board", "de/climbing", "de/capturing", "de/notation"],
    cta: { label: "Das Brett", slug: "de/board" },
    es: "reglas",
  },
  {
    slug: "de/board",
    lang: "de",
    nav: "Das Brett",
    title: "Das XIMA-Brett: Etagen, Felder und Koordinaten",
    description:
      "Das XIMA-Brett erklärt: eine Pyramide mit vier Etagen auf 13 × 13 Feldern, Koordinaten von A1 bis M13, innere und äußere Ringe, Seiten, Ecken und die Spitze G7.",
    h1: "Das Brett",
    intro: "Wer die Etagen der Pyramide und die Namen der Felder kennt, kann jede Partie lesen und seinen Weg zur Spitze planen.",
    sections: [
      {
        h: "Von oben: 13 × 13 Felder",
        p: ["Die Spalten heißen A bis M, die Reihen 1 bis 13. A1 ist eine Ecke, M13 die gegenüberliegende, und G7, die Mitte, ist die Spitze."],
        figure: "tablero-vista-superior",
      },
      {
        h: "Von der Seite: vier Etagen",
        table: {
          head: ["Etage", "Bereich (von oben)", "Felder"],
          rows: [
            ["1.", "Die zwei äußeren Reihen und Spalten", "88"],
            ["2.", "Quadrat C3–K11 ohne E5–I9", "56"],
            ["3.", "Quadrat E5–I9 ohne die Spitze", "24"],
            ["4. (Spitze)", "Nur G7", "1"],
          ],
        },
        after: ["Jede Etage liegt zwei Blockhöhen über der vorherigen, darum braucht man zum Aufsteigen immer eine Stufe."],
        figure: "tablero-vista-lateral",
      },
      {
        h: "Ringe, Seiten und Ecken",
        p: [
          "Jede Etage außer der Spitze hat einen äußeren Ring (den Rand) und einen inneren Ring (die Reihe an der nächsten Etage). Auf den inneren Feldern baut man die Stufen.",
          "Die Pyramide hat vier Seiten: A1-M1-G7, M1-M13-G7, M13-A13-G7 und A13-A1-G7. Äußere Ecken sind A1, M1, M13 und A13, innere Ecken B2, L2, L12 und B12. Die oberen Diagonalen (A1-M13 und M1-A13) sind der kürzeste Weg zur Spitze, lassen sich aber am leichtesten blockieren.",
        ],
        figure: "caras-y-casillas",
      },
    ],
    faqs: [["Wie viele Felder hat das XIMA-Brett?", "169 von oben: 88 auf der ersten Etage, 56 auf der zweiten, 24 auf der dritten und 1 auf der Spitze."]],
    related: ["de/climbing", "de/notation", "de/rules"],
    cta: { label: "Aufsteigen", slug: "de/climbing" },
    es: "reglas/tablero-y-casillas",
  },
  {
    slug: "de/climbing",
    lang: "de",
    nav: "Aufsteigen",
    title: "Aufsteigen in XIMA: Höhen und Stufen",
    description:
      "Die wichtigste Regel in XIMA: wie hoch ein Block pro Schritt steigt, warum man für eine neue Etage eine Stufe braucht und wie man zwei Etagen auf einmal schafft.",
    h1: "Aufsteigen",
    intro: "Bei XIMA gewinnt vor allem, wer früher aufsteigt als der Gegner. Dafür musst du eine Regel verstehen, die Höhenregel, und lernen, Stufen zu bauen.",
    sections: [
      {
        h: "Die Höhenregel",
        p: [
          "Pro Schritt steigt ein Block höchstens auf seine eigene Höhe, also auf etwas, das einen Block hoch ist. Absteigen darf er beliebig weit.",
          "Jede Etage liegt zwei Blockhöhen über der vorherigen. Ein Block auf der ersten Etage kommt also nicht direkt auf die zweite: Er braucht etwas dazwischen.",
        ],
        figure: "tablero-vista-lateral",
      },
      {
        h: "Die Stufe",
        p: [
          "Dieses Etwas ist ein anderer Block auf einem inneren Feld an der höheren Etage. Der aufsteigende Block geht über die Stufe hinweg und ist im selben Zug eine Etage höher.",
          "Beispiel: Blöcke auf A1 und B2. Der Block auf A1 zieht diagonal: über B2 (eine Höhe hoch) auf C3 (noch eine Höhe, jetzt auf der zweiten Etage). Die Stufe kann dir oder dem Gegner gehören.",
        ],
        figure: "subir-con-escalon",
      },
      {
        h: "Doppeln und Doppelstufen",
        p: [
          "Ein Block, der auf einem anderen steht, hat die nötige Höhe schon: Stellst du einen eigenen Block auf einen eigenen, steigt der obere im nächsten Zug ohne Stufe auf. Und mit einer Stufe auf der ersten und einer auf der zweiten Etage in derselben Linie steigt ein Block zwei Etagen auf einmal: von A1 nach E5 über B2 und D4.",
        ],
      },
    ],
    related: ["de/board", "de/capturing", "de/strategy"],
    cta: { label: "Beherrschen und Schlagen", slug: "de/capturing" },
    es: "reglas/subir-bloques",
  },
  {
    slug: "de/capturing",
    lang: "de",
    nav: "Beherrschen und Schlagen",
    title: "Beherrschen und Schlagen in XIMA",
    description:
      "Wann ein Block in XIMA einen anderen beherrscht, wann er ihn schlägt, und was „Sandwich“ und „Zerquetschen“ bedeuten. Mit klaren Beispielen.",
    h1: "Beherrschen und Schlagen",
    intro: "In XIMA musst du keine gegnerischen Figuren schlagen, um zu gewinnen, aber Beherrschen und Schlagen bremst den Gegner und nimmt ihm Stufen weg.",
    sections: [
      {
        h: "Beherrschen",
        p: [
          "Ein Block beherrscht, wenn er seinen Zug auf einem oder mehreren Blöcken beendet. Alle Blöcke darunter, egal welcher Farbe, können nicht ziehen, solange er oben steht. Zieht er weg, sind sie wieder frei.",
        ],
      },
      {
        h: "Schlagen",
        p: [
          "Kommt ein Block in eine Säule, zählt man die Blöcke jeder Farbe mitsamt dem neuen. Hat keine Farbe mehr als deine, werden alle Farben mit weniger Blöcken geschlagen und verlassen das Spiel (außer dem Spezialblock, der gerettet werden kann).",
        ],
        list: [
          "Blau auf Rot: 1 gegen 1. Rot ist beherrscht, aber nicht geschlagen.",
          "Blau kommt auf eine Säule mit Rot und Blau: 2 gegen 1. Rot ist geschlagen.",
          "Blau kommt auf zwei rote Blöcke: 1 gegen 2. Die roten sind nur beherrscht.",
        ],
        after: [
          "Achte auf die Höhe: Um auf einer Säule aus zwei Blöcken zu landen, musst du von weiter oben kommen, zum Beispiel von der höheren Etage herab. Von derselben Höhe aus steigst du nur auf einen einzelnen Block.",
        ],
        figure: "dominacion-captura",
      },
      {
        h: "Arten des Schlagens",
        list: [
          "Sandwich: Einer deiner Blöcke wird vom Gegner beherrscht, und ein weiterer eigener Block kommt oben drauf. Der Gegner ist zwischen deinen beiden Blöcken in der Minderheit.",
          "Zerquetschen: Derselbe Spieler beherrscht eine Säule erneut, indem er einen zweiten Block auf seinen eigenen setzt.",
          "Gemischt: beides gleichzeitig in derselben Säule.",
        ],
      },
    ],
    related: ["de/rules", "de/climbing", "de/strategy"],
    cta: { label: "Strategie", slug: "de/strategy" },
    es: "reglas/dominacion-y-captura",
  },
  {
    slug: "de/game-modes",
    lang: "de",
    nav: "Spielmodi",
    title: "XIMA-Spielmodi: Wissenschaft, Zufall, Cienzar und mehr",
    description:
      "Die vier Spielmodi von XIMA: „Wissenschaft“ ohne Würfel, „Zufall“ mit zwei Würfeln, „Cienzar“ (Wahl in jedem Zug) und „Wissenschaft gegen Zufall“. Welchen wählen?",
    h1: "Spielmodi",
    intro: "Die Brettregeln sind in allen Modi gleich. Es ändert sich nur, wer entscheidet, wie viele Schritte du gehst: du, die Würfel oder beide.",
    sections: [
      {
        h: "Die vier Modi",
        table: {
          head: ["Modus", "Würfel", "Ideal für"],
          rows: [
            ["Wissenschaft (Ciencia)", "Keine", "Reine Strategie, wie beim Schach."],
            ["Zufall (Azar)", "Zwei pro Zug", "Familienspiele und Spieler unterschiedlicher Stärke."],
            ["Cienzar", "Wahl in jedem Zug", "Rechnen und Risiko kombinieren."],
            ["Wissenschaft gegen Zufall (Contrazar)", "Nur die Zufallsseite", "Zwei Spielstile gegeneinander."],
          ],
        },
      },
      {
        h: "So funktionieren die Würfel",
        p: [
          "Jeder Würfel ist ein eigener Zug: Der gewählte Block muss genau so viele Schritte geradeaus gehen. Du kannst beide Würfel für verschiedene Blöcke oder für denselben Block nutzen, auch mit Richtungswechsel, in beliebiger Reihenfolge. Beim Einsetzen zählt das Randfeld als erster Schritt.",
          "Bei „Cienzar“ machst du zu Beginn deines Zuges entweder einen freien Zug wie bei „Wissenschaft“ oder würfelst mit beiden Würfeln und machst zwei Züge.",
        ],
      },
    ],
    related: ["de/rules", "de/how-to-play", "de/strategy"],
    cta: { label: "So wird gespielt", slug: "de/how-to-play" },
    es: "modos-de-juego",
  },
  {
    slug: "de/notation",
    lang: "de",
    nav: "Notation",
    title: "XIMA-Notation: so notiert man eine Partie",
    description:
      "Die „sehr vereinfachte“ algebraische Notation von XIMA: lange und kurze Form, „-“ beim Einsetzen, „=“ für den Spezialblock, Schlagen und Würfel, mit Beispielen.",
    h1: "Notation",
    intro: "Weil alle Blöcke gleich ziehen, ist eine XIMA-Partie leichter zu notieren als eine Schachpartie: Meist genügt das Feld, auf dem der Block ankommt.",
    sections: [
      {
        h: "So notiert man einen Zug",
        list: [
          "Die Zugnummer mit Punkt.",
          "Das Zielfeld in Großbuchstaben: D3.",
          "Ein Bindestrich davor, wenn ein normaler Block eingesetzt wird: -C2.",
          "Ein Gleichheitszeichen, wenn der Spezialblock eingesetzt wird: =C7.",
          "Ein x beim Schlagen: C4xC10.",
          "Mit Würfeln zuerst die gewürfelten Zahlen und ein Schrägstrich: 4/-A4.",
        ],
      },
      {
        h: "Lange und kurze Form",
        p: [
          "Die lange Form nennt Start- und Zielfeld (C3D3) und ist für Anfänger einfacher. Die kurze Form nennt nur das Ziel (D3). Können zwei Blöcke dasselbe Feld erreichen, fügt man die Startspalte (BD11) oder Startreihe (8C7) hinzu.",
          "Das Erreichen der Spitze notiert man als G7, Xima, Cima, (*), 1-0 oder Gana. 1-0 heißt, der erste Spieler hat gewonnen, 0-1 der zweite.",
        ],
      },
    ],
    related: ["de/board", "de/rules", "de/openings"],
    cta: { label: "Eröffnungen", slug: "de/openings" },
    es: "sistema-de-anotacion",
  },
  {
    slug: "de/strategy",
    lang: "de",
    nav: "Strategie",
    title: "XIMA-Strategie: die wichtigsten Ideen für mehr Siege",
    description:
      "XIMA-Strategie in einer Anleitung: der Kampf um die zweite Etage, wann der Spezialblock ins Spiel kommt, die zwei Zentren, die Initiative und das Endspiel.",
    h1: "Strategie",
    intro: "Eine XIMA-Partie hat wie Schach drei Phasen: Eröffnung, Mittelspiel und Endspiel. Alle drei drehen sich um dasselbe: früher aufsteigen als der Gegner.",
    sections: [
      {
        h: "Zuerst aufsteigen",
        p: [
          "Das XIMA-Buch fasst es so zusammen: Die besten Chancen auf die Spitze hat, wer zuerst mindestens zwei Blöcke, darunter den Spezialblock, auf die dritte Etage bringt. Dafür braucht er vorher mindestens drei Blöcke auf der zweiten Etage, früher als der Gegner.",
        ],
      },
      {
        h: "Zehn Grundsätze",
        list: [
          "Jedes Tempo zählt: In der Eröffnung sind zwei Tempi oft mehr wert als ein Block.",
          "Geh in den ersten Zügen nicht auf Blockjagd.",
          "Bereite immer mehr als einen Weg nach oben vor.",
          "Bring den Spezialblock nicht zu früh, sondern hoch und geschützt, meist als 6. bis 8. Block.",
          "Behalte einen normalen Block für das Endspiel neben dem Brett.",
          "Beherrsche die Stufen des Gegners, um seinen Aufstieg zu stoppen.",
          "Kämpfe um das mittlere Zentrum (2. Etage) und das hohe Zentrum (3. Etage).",
          "Koordiniere deine Blöcke: Lose Blöcke ergeben eine schlechte Stellung.",
          "Halte die Initiative mit Drohungen, auf die der Gegner antworten muss.",
          "Im Endspiel gilt: Zwei Blöcke schlagen einen.",
        ],
      },
      {
        h: "Endspiel und Opposition",
        p: [
          "Ein Spezialblock darf nie über den anderen hinweggehen. Stehen beide nebeneinander (in Opposition), versperren sie sich gegenseitig Linien. Im Endspiel nutzt man das, um einen normalen Stützblock in einen „toten Winkel“ zu stellen, den der gegnerische Spezialblock nicht erreicht, und auf G7 zu steigen.",
        ],
      },
    ],
    related: ["de/openings", "de/climbing", "de/cimax"],
    cta: { label: "Eröffnungen", slug: "de/openings" },
    es: "estrategia",
  },
  {
    slug: "de/openings",
    lang: "de",
    nav: "Eröffnungen",
    title: "XIMA-Eröffnungen: die ersten Züge und ihre Namen",
    description:
      "XIMA-Eröffnungen nach dem Buch: warum es nur 13 verschiedene Startfelder gibt und wie die Eröffnungen Liz, Susy, Muiño, Tanu, Párragueña, Capablanca und Yosdel aussehen.",
    h1: "Eröffnungen",
    intro: "Ziel einer guten Eröffnung ist, die Blöcke ins Spiel zu bringen, um auf den Gegner reagieren und so schnell wie möglich aufsteigen zu können.",
    sections: [
      {
        h: "Nur 13 verschiedene Eröffnungen",
        p: [
          "Es gibt 176 mögliche erste Züge: 88 Felder der ersten Etage, jeweils mit normalem Block oder Spezialblock. Die vier Seiten der Pyramide sind aber gleich und jede ist symmetrisch, sodass viele Züge dieselbe Idee sind. Am Ende bleiben 13 wirklich verschiedene Startfelder.",
        ],
      },
      {
        h: "Eröffnungen mit Namen",
        table: {
          head: ["Eröffnung", "Felder", "Idee"],
          rows: [
            ["Yosdel", "A1, M1, M13, A13", "Natürliche Stufe auf B2 mit einem einzigen, schnellen, aber leicht blockierbaren Weg."],
            ["Capablanca", "A2, B1, L1, M2, M12, L13, B13, A12", "Stufe auf C3 mit zwei Wegen. Wohl die ausgewogenste."],
            ["Párragueña", "A3, C1, K1, M3, M11, K13, C13, A11", "Zwei natürliche Stufen (C3 und D3) mit fünf Wegen."],
            ["Tanu (ukrainisch)", "A4, D1, J1, M4, M10, J13, D13, A10", "Zwei natürliche und eine künstliche Stufe, acht Wege; stark im Endspiel."],
            ["Muiño", "A5, E1, I1, M5, M9, I13, E13, A9", "Drei natürliche Stufen, neun Wege; aggressive Kontrolle der 2. Etage."],
            ["Susy", "A6, F1, H1, M6, M8, H13, F13, A8", "Eine der dynamischsten Eröffnungen."],
            ["Liz", "A7, G1, M7, G13", "Zentrale Eröffnung direkt zur Spitze; technisch und präzise."],
          ],
        },
      },
    ],
    related: ["de/strategy", "de/notation", "de/history-of-xima"],
    cta: { label: "Strategie", slug: "de/strategy" },
    es: "estrategia/aperturas-xima",
  },
  {
    slug: "de/cimax",
    lang: "de",
    nav: "CIMAX-Wertung",
    title: "CIMAX: das Wertungssystem von XIMA",
    description:
      "Was CIMAX ist und wie es funktioniert: Jeder Spieler startet mit 1000 Punkten und gewinnt oder verliert Punkte je nach Stärke, Leistung, Zeit und Zügen einer Partie.",
    h1: "CIMAX-Wertung",
    intro: "CIMAX ist die Wertung, die dein Niveau in XIMA misst. Sie ähnelt der Elo-Zahl im Schach, berücksichtigt aber nicht nur Sieg oder Niederlage, sondern auch, wie du gespielt hast.",
    sections: [
      {
        h: "Das Wichtigste",
        list: [
          "Jeder Spieler startet mit 1000 CIMAX-Punkten.",
          "Für jeden Spielmodus gibt es einen eigenen CIMAX-Wert.",
          "Was der Sieger gewinnt, verliert der Verlierer.",
          "Je stärker, schneller und kürzer der Sieg, desto mehr Punkte bewegen sich.",
        ],
      },
      {
        h: "Die Formel",
        p: [
          "Laut dem XIMA-Buch setzt sich die Spielstärke (FJ) aus dem Wert der Blöcke auf und neben dem Brett, den geschlagenen Blöcken und einem Bonus für den Sieg (16 Punkte für das Erreichen der Spitze) oder eine Aufgabe des Gegners zusammen. Das Spielniveau (NJ) ist die Summe der Stärken beider Spieler. Die Leistung (PJ) ist die Stärke geteilt durch 2 hoch effektive Zeit (verbrauchte ÷ vereinbarte Zeit) mal Anzahl der Züge.",
          "Übertragener CIMAX = NJ × (höhere Leistung + 1) ÷ (niedrigere Leistung + 1) × (CIMAX des Verlierers ÷ CIMAX des Siegers).",
        ],
      },
    ],
    related: ["de/strategy", "de/rules"],
    cta: { label: "Strategie", slug: "de/strategy" },
    es: "estadisticas/cimax",
  },
  {
    slug: "de/history-of-xima",
    lang: "de",
    nav: "Geschichte von XIMA",
    title: "Die Geschichte von XIMA: ein Brettspiel aus Kuba",
    description:
      "XIMA entstand 2010 in Havanna, erfunden von Yosdel Vicente Muiño Acevedo und Liz Power Suárez. Woher das Spiel kommt, sein Name und seine Idee.",
    h1: "Geschichte von XIMA",
    intro: "XIMA entstand 2010 in Havanna, Kuba. Die Erfinder Yosdel Vicente Muiño Acevedo und Liz Power Suárez wollten ein Brettspiel schaffen, das nach oben gespielt wird.",
    sections: [
      {
        h: "Ein Versprechen aus Liebe",
        p: [
          "Wie das Buch „Cómo llegar a la Xima“ erzählt, begann alles mit einem Versprechen: Liz das beste Brettspiel überhaupt zu schenken, eines, mit dem man mehr Zeit gemeinsam zu Hause und mit der Familie verbringt. Bis zur endgültigen Form vergingen etwa zwölf Jahre.",
        ],
      },
      {
        h: "Vom Schach zur Pyramide",
        p: [
          "Vor XIMA schrieb Yosdel ein Kinderbuch, „El rey peón“, in dem Schachfiguren lebendig werden. Dafür musste er sich vorstellen, wie Schach erfunden worden sein könnte, und daraus entstand die Frage, die alles änderte: Und wenn das Brett nicht flach wäre? Die Antwort war die geheimnisvollste Form überhaupt: die Pyramide.",
        ],
      },
      {
        h: "Von Cima zu Xima",
        p: [
          "Das Spiel hieß zuerst Cima, spanisch für „Gipfel“, nach seinem Ziel. Später wurde daraus XIMA, aber das höchste Feld heißt bis heute die Spitze.",
        ],
      },
    ],
    related: ["de/what-is-xima", "de/openings"],
    cta: { label: "Was ist XIMA", slug: "de/what-is-xima" },
    es: "historia-de-xima",
  },
];
